export interface IWishlistItem {
  readonly product_id: string
  readonly color: string
  readonly website_light_product?: any
  /**
   * SVD superset fields (Phase 2). Optional + additive — legacy (non-SVD) entries never carry them,
   * so legacy behaviour + the spid+color `getWishListId` are unchanged. The SVD flow backfills these
   * and dedups on `getSvdWishListId`. Kept alongside spid+color so a browser flipping SVD↔non-SVD
   * works both ways.
   */
  readonly productId?: string
  readonly selectedAttributes?: Record<string, string>
  /**
   * SVD-only: true once this entry's productId+selectedAttributes have been confirmed by an
   * authoritative `/v2/get-active-wishlist-products` read (set by `syncSvdWishlistFromCards`).
   * Lets the SVD login-merge push only genuinely-unsynced local entries instead of re-pushing the
   * whole wishlist to `/v2/bulk-update-user-wishlist` on every page view. Legacy code never sets or
   * reads this field.
   */
  readonly svdSynced?: boolean
}

export enum ACTION {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

/**
 * Order-independent serialization of a VDA attribute set for the SVD wishlist key. **Case-sensitive**
 * on values (only sort + trim) so the client identity mirrors the backend's exact-string wishlist key
 * — we never lower-case what could diverge from the server's own dedup. Cross-system casing
 * inconsistencies are the backend's to fix; we forward + compare verbatim.
 */
export function serializeSelectedAttributes(selectedAttributes?: Record<string, string>): string {
  if (!selectedAttributes) return ''
  return Object.keys(selectedAttributes)
    .sort()
    .map(key => `${key.trim()}:${String(selectedAttributes[key]).trim()}`)
    .join('|')
}

export class WishListItem implements IWishlistItem {
  readonly product_id: string
  readonly color: string
  readonly website_light_product?: any // TODO: Update this after website light product is fully migrated to v2
  readonly productId?: string
  readonly selectedAttributes?: Record<string, string>
  readonly svdSynced?: boolean

  constructor({
    product_id,
    color,
    website_light_product = {},
    productId,
    selectedAttributes,
    svdSynced,
  }: IWishlistItem) {
    this.product_id = product_id
    this.color = color
    this.website_light_product = website_light_product
    this.productId = productId
    this.selectedAttributes = selectedAttributes
    this.svdSynced = svdSynced
    return {
      ...this,
    }
  }

  static factEquals(wishListItem1: WishListItem, wishListItem2: WishListItem): boolean {
    return this.getWishListId(wishListItem1) === this.getWishListId(wishListItem2)
  }

  /** Legacy identity — spid + color. Unchanged; drives all non-SVD dedup. */
  static getWishListId(wishListItem: WishListItem): string {
    return `${wishListItem.product_id}_${wishListItem.color}`
  }

  /** SVD identity — internal productId + serialized selectedAttributes (Phase 2). */
  static getSvdWishListId(wishListItem: Pick<IWishlistItem, 'productId' | 'selectedAttributes'>): string {
    return `${wishListItem.productId}_${serializeSelectedAttributes(wishListItem.selectedAttributes)}`
  }
}

export interface SharedWishlistRes {
  user_name?: string
  wishlist?: WishListItem[]
}
